import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FundOutlined,
  LineChartOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import type { NocActiveSession, NocOverview } from '@goelectric/shared-types';
import { AnimatedNumber } from './AnimatedNumber';
import { NocKpiCard } from './NocKpiCard';
import type { NocLiveMode } from '../hooks/useNocLive';
import { deriveRevenue, networkAvailabilityPct } from '../utils/derive';
import { formatCop, formatInteger, formatKwh, formatKw, formatPercent } from '../utils/format';

interface NocHeroProps {
  overview: NocOverview | null;
  sessions: NocActiveSession[];
  mode: NocLiveMode;
  lastUpdated: string | null;
  loading: boolean;
}

/** Indicador de origen/actualidad de los datos (polling vs SSE). */
function LiveIndicator({ mode, lastUpdated }: { mode: NocLiveMode; lastUpdated: string | null }) {
  const label =
    mode === 'sse'
      ? 'Stream en vivo'
      : mode === 'polling'
        ? 'Actualizando cada 3 s'
        : 'Conectando…';
  const time = lastUpdated
    ? new Date(lastUpdated).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <span className="noc-live" data-mode={mode}>
      <span className="noc-live__pulse" aria-hidden="true" />
      <SyncOutlined spin={mode === 'connecting'} aria-hidden="true" />
      <span className="noc-live__label">{label}</span>
      <Tooltip title="Última lectura del backend">
        <span className="noc-live__time">
          <ClockCircleOutlined aria-hidden="true" /> {time}
        </span>
      </Tooltip>
    </span>
  );
}

/** Hero oscuro del NOC: ingresos protagonistas + KPIs de red. */
export function NocHero({ overview, sessions, mode, lastUpdated, loading }: NocHeroProps) {
  const revenue = deriveRevenue(overview, sessions);
  const availability = networkAvailabilityPct(overview);

  return (
    <section className="noc-hero" aria-busy={loading}>
      <div className="noc-hero__top">
        <div>
          <Typography.Title level={4} className="noc-hero__title">
            Centro de Operaciones de Red
          </Typography.Title>
          <Typography.Text className="noc-hero__subtitle">
            Monitoreo en vivo de la red de carga · Colombia
          </Typography.Text>
        </div>
        <LiveIndicator mode={mode} lastUpdated={lastUpdated} />
      </div>

      <div className="noc-hero__revenue">
        <span className="noc-hero__revenue-label">
          Ingresos de hoy <em>(estimado)</em>
        </span>
        <span className="noc-hero__revenue-value">
          <AnimatedNumber value={revenue.revenueTodayCop} format={formatCop} />
        </span>
        <span className="noc-hero__revenue-hint">
          {formatKwh(revenue.energyTodayKwh)} × {formatInteger(revenue.tariffCopPerKwh)} COP/kWh · provisional
          {revenue.revenueInProgressCop > 0 && (
            <> · +{formatCop(revenue.revenueInProgressCop)} en curso</>
          )}
        </span>
      </div>

      <div className="noc-hero__kpis">
        <NocKpiCard
          dark
          label="Ingresos acumulados"
          value="—"
          hint="Requiere historial de sesiones (US 2.9)"
          icon={<LineChartOutlined />}
        />
        <NocKpiCard
          dark
          label="Potencia total"
          value={<AnimatedNumber value={overview?.power.totalInstantKw ?? 0} format={formatKw} />}
          hint={overview?.power.estimated ? 'Sin telemetría en vivo (TG-93/94)' : 'Medida en vivo'}
          icon={<ThunderboltOutlined />}
        />
        <NocKpiCard
          dark
          label="Energía hoy"
          value={<AnimatedNumber value={revenue.energyTodayKwh} format={(value) => formatKwh(value)} />}
          hint="Piso: Transactions.totalKwh"
          icon={<FundOutlined />}
        />
        <NocKpiCard
          dark
          label="Sesiones activas"
          value={<AnimatedNumber value={overview?.sessions.active ?? 0} format={(value) => formatInteger(value)} />}
          hint={`${sessions.length} en detalle`}
          icon={<ThunderboltOutlined />}
        />
        <NocKpiCard
          dark
          label="Disponibilidad de red"
          value={<AnimatedNumber value={availability} format={formatPercent} />}
          hint={`${overview?.stations.online ?? 0}/${overview?.stations.total ?? 0} estaciones online`}
          icon={<CheckCircleOutlined />}
        />
      </div>
    </section>
  );
}
